<template>
  <div>
    <b-card-title
      class="slogan-text mb-1"
      title-tag="h2"
    >
      {{ $t('auth.phone.title') }}
    </b-card-title>
    <b-card-text
      class="slogan-sub-text mb-2"
      v-html="$t('auth.phone.description')"
    />
    <!-- form -->
    <validation-observer
      ref="loginForm"
      #default="{invalid}"
    >
      <b-form
        class="auth-login-form mt-2"
        @submit.prevent="phoneCheck"
      >
        <b-form-group
          label-for="login-phone"
        >
          <validation-provider
            #default="{ errors }"
            :name="$t('Mobile phone number')"
            vid="phone"
            rules="required"
          >
            <vue-tel-input
              v-model="phone"
              mode="auto"
              :inputOptions="{placeholder: $t('Mobile phone number')}"
              validCharactersOnly
              :styleClasses="['phone-input']"
              @open="onDropdownOpen(true)"
              @close="onDropdownOpen(false)"
              @country-changed="countryChanged"
              @validate="phoneValidate"
            >
              <template v-slot:arrow-icon>
                <span class="font-small-3 mr-05">
                  {{ country ? '+' + country.dialCode : '' }}
                </span>
                <font-awesome-icon
                  v-if="open"
                  icon="fa-solid fa-chevron-up"
                />
                <font-awesome-icon
                  v-else
                  icon="fa-solid fa-chevron-down"
                />
              </template>
            </vue-tel-input>
            <small v-if="!registrationDisabled" class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <div
          v-if="registrationDisabled"
          class="registration-disabled-alert"
          role="alert"
        >
          <div class="registration-disabled-alert__icon">
            <font-awesome-icon icon="fa-solid fa-circle-exclamation" />
          </div>
          <div class="registration-disabled-alert__text">
            {{ $t('auth.phone.Registration disabled') }}
          </div>
        </div>

        <b-button
          v-if="registrationDisabled"
          class="mt-2 btn-primary2"
          type="button"
          variant="primary"
          block
          @click="goBackToSocial"
        >
          {{ $t('Got it') }}
        </b-button>
        <b-button
          v-else
          class="mt-2 btn-primary2"
          type="submit"
          variant="primary"
          block
          :disabled="loading || invalid"
        >
          <b-spinner
            v-if="loading"
            small
          />
          {{ $t('Continue') }}
        </b-button>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BForm, BFormGroup, BCardTitle, BCardText, BButton, BSpinner,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { throwDefaultError } from '@/helpers/helpers'

export default {
  components: {
    BButton,
    BCardText,
    BCardTitle,
    BFormGroup,
    BForm,
    BSpinner,

    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      country: null,
      loading: false,
      open: false,
      phone: '',
      valid: false,
      registrationDisabled: false,
    }
  },
  computed: {
    phoneWithCountryCode() {
      return this.country ? `+${this.country.dialCode}${this.phone.replace(/ /g, '')}` : this.phone.replace(/ /g, '')
    },
  },
  created() {},
  methods: {
    countryChanged(country) {
      this.country = country
    },
    onDropdownOpen(open) {
      this.open = open
    },
    phoneCheck() {
      if (!this.valid) {
        this.$refs.loginForm.setErrors({
          phone: this.$t('Check phone number'),
        })
        return false
      }
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.loading = true
          this.$emit('updatePhone', this.phoneWithCountryCode)
          useJwt.phoneCheck(this.phoneWithCountryCode, this.$i18n.locale)
            .then(() => {
              this.$store.dispatch('auth/setAuthType', 'phone')
              useJwt.forgotPassword(this.phoneWithCountryCode, this.$i18n.locale)
                .catch(pinError => {
                  throwDefaultError(this.$toast, pinError)
                })
                .finally(() => {
                  this.$emit('updateActiveComponent', 'Password')
                  this.$emit('updateBackButton', 'Phone')
                  this.loading = false
                })
            })
            .catch(error => {
              this.loading = false
              if (error?.response?.data?.header?.error?.errorCode == '305') {
                this.registrationDisabled = true
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: this.$t('You should check your login information and try again'),
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
    phoneValidate(status) {
      this.valid = status ? status.valid : false
      this.registrationDisabled = false
      this.$refs.loginForm.reset()
    },
    goBackToSocial() {
      this.registrationDisabled = false
      this.$emit('updateActiveComponent', 'SocialButtons')
      this.$emit('updateBackButton', 'SocialButtons')
    },
  },
}
</script>

<style src="vue-tel-input/dist/vue-tel-input.css"></style>

<style lang="scss" scoped>
.registration-disabled-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  margin-top: 16px;
  background: rgba(255, 78, 78, 0.12);
  border: 1px solid rgba(255, 78, 78, 0.45);
  border-radius: 10px;
  color: #ff4e4e;

  &__icon {
    flex-shrink: 0;
    font-size: 18px;
    line-height: 1.2;
    display: flex;
    align-items: center;
  }

  &__text {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
  }
}
</style>
